/* eslint-disable no-console */
import React, { useState, useRef, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  Button, 
  Box, 
  Typography, 
  IconButton,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Slide,
  Fab
} from '@mui/material';
import { 
  CameraAlt, 
  Cameraswitch, 
  Close, 
  Check,
} from '@mui/icons-material';
import { TransitionProps } from '@mui/material/transitions';

interface SimpleCameraProps {
  open: boolean;
  onClose: () => void;
  onCapture: (imageBlob: Blob, fileName: string) => void;
}

// Slide transition for mobile
const Transition = React.forwardRef(function Transition(
  props: TransitionProps & {
    children: React.ReactElement;
  },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

/**
 * A responsive camera component optimized for mobile devices
 */
const SimpleCamera: React.FC<SimpleCameraProps> = ({ open, onClose, onCapture }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [usingFrontCamera, setUsingFrontCamera] = useState(false);

  // Responsive configuration
  const getResponsiveConfig = () => ({
    dialogFullScreen: isMobile,
    dialogMaxWidth: isMobile ? false : 'md',
    dialogHeight: isMobile ? '100vh' : isTablet ? '90vh' : '80vh',
    dialogMaxHeight: isMobile ? '100vh' : '600px',
    buttonSize: isMobile ? 'large' : 'medium',
    fabSize: isMobile ? 'large' : 'medium',
    titleFontSize: isMobile ? '1.1rem' : '1.25rem',
    bodyFontSize: isMobile ? '0.875rem' : '1rem',
    padding: isMobile ? 1 : 2,
    buttonPadding: isMobile ? 2 : 2
  });

  const config = getResponsiveConfig();
  
  // Stop camera stream
  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  };
  
  // Switch between front and back cameras
  const switchCamera = () => {
    setUsingFrontCamera(!usingFrontCamera);
  };
  
  // Capture a photo
  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) {
      console.error('Video or canvas ref is not available');
      return;
    }
    
    const video = videoRef.current;
    const canvas = canvasRef.current;
    
    // Get video dimensions
    const videoWidth = video.videoWidth || 640;
    const videoHeight = video.videoHeight || 480;
    
    console.log('Capturing photo with dimensions:', videoWidth, 'x', videoHeight);
    
    // Set canvas size
    canvas.width = videoWidth;
    canvas.height = videoHeight;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      console.error('Could not get canvas context');
      return;
    }
    
    // Mirror image if using front camera
    if (usingFrontCamera) {
      ctx.translate(videoWidth, 0);
      ctx.scale(-1, 1);
    }
    
    // Draw the video frame to the canvas
    ctx.drawImage(video, 0, 0, videoWidth, videoHeight);
    
    // Reset transformation
    if (usingFrontCamera) {
      ctx.setTransform(1, 0, 0, 1, 0, 0);
    }
    
    // Convert to data URL
    try {
      const dataUrl = canvas.toDataURL('image/jpeg', 0.95);
      setCapturedImage(dataUrl);
      console.log('Photo captured successfully');
    } catch (e) {
      console.error('Error creating data URL:', e);
      setError('Failed to capture photo');
    }
  };
  
  // Save the captured photo
  const savePhoto = async () => {
    if (!capturedImage) {
      console.error('No captured image to save');
      return;
    }
    
    try {
      // Convert data URL to blob
      const response = await fetch(capturedImage);
      const blob = await response.blob();
      
      // Generate filename
      const timestamp = new Date().toISOString().replace(/:/g, '-');
      const fileName = `inspection-photo-${timestamp}.jpg`;
      
      // Send to parent component
      onCapture(blob, fileName);
      onClose();
    } catch (e) {
      console.error('Error saving photo:', e);
      setError('Failed to save photo');
    }
  };
  
  // Retake the photo
  const retakePhoto = () => {
    setCapturedImage(null);
  };
  
  // Initialize camera
  useEffect(() => {
    let mounted = true;
    
    async function initCamera() {
        setLoading(true);
        setError(null);
        
        try {
          if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
            throw new Error('Camera API not supported');
          }
          
          // Stop any existing stream
          stopCamera();
          
          // Try to get the camera with the current facing mode
          const facingMode = usingFrontCamera ? 'user' : 'environment';
          console.log(`Requesting camera with facing mode: ${facingMode}`);
          
          const stream = await navigator.mediaDevices.getUserMedia({
            video: { 
              facingMode,
              // Add mobile-optimized constraints
              width: { ideal: isMobile ? 1280 : 1920 },
              height: { ideal: isMobile ? 720 : 1080 }
            },
            audio: false
          });
          
          if (!mounted) return;
          
          // Log tracks for debugging
          console.log('Camera tracks:', stream.getVideoTracks().map(t => t.label));
          
          streamRef.current = stream;
          
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            
            // Force width/height to ensure styling works
            if (videoRef.current.offsetWidth === 0) {
              videoRef.current.style.width = '100%';
              videoRef.current.style.height = 'auto';
            }
            
            // Handle video loaded
            videoRef.current.onloadedmetadata = () => {
              if (!videoRef.current) return;
              console.log('Video metadata loaded', videoRef.current.videoWidth, videoRef.current.videoHeight);
              
              // Try to play - this might fail on some browsers without interaction
              videoRef.current.play()
                .then(() => {
                  console.log('Video playing successfully');
                  setLoading(false);
                })
                .catch(e => {
                  console.error('Video play error (will retry with interaction):', e);
                  setError('Tap anywhere to start camera');
                  
                  // Add click handler to the document to try playing on interaction
                  const clickHandler = () => {
                    if (videoRef.current) {
                      videoRef.current.play()
                        .then(() => {
                          console.log('Video started after interaction');
                          setLoading(false);
                          setError(null);
                        })
                        .catch(err => {
                          console.error('Still failed to play video after interaction:', err);
                          setError('Unable to start camera. Please try again or use another browser.');
                        });
                    }
                    document.removeEventListener('click', clickHandler);
                  };
                  
                  document.addEventListener('click', clickHandler);
                });
            };
          }
        } catch (err) {
          console.error('Camera initialization error:', err);
          if (mounted) {
            setError('Could not access camera. Please check permissions and try again.');
            setLoading(false);
          }
        }
      }

    if (open) {
      initCamera();
    }
    
    return () => {
      mounted = false;
      stopCamera();
    };
    
  }, [open, usingFrontCamera, isMobile]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={config.dialogMaxWidth as any}
      fullWidth={!isMobile}
      fullScreen={config.dialogFullScreen}
      TransitionComponent={isMobile ? Transition : undefined}
      PaperProps={{
        sx: {
          height: config.dialogHeight,
          maxHeight: config.dialogMaxHeight,
          display: 'flex',
          flexDirection: 'column',
          margin: { xs: 0, sm: 2 },
          borderRadius: { xs: 0, sm: 2 }
        }
      }}
      sx={{
        '& .MuiDialog-container': {
          alignItems: { xs: 'flex-end', sm: 'center' }
        }
      }}
    >
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        p: config.padding,
        minHeight: { xs: 56, sm: 'auto' }
      }}>
        <Typography 
          variant="h6"
          sx={{ 
            fontSize: config.titleFontSize,
            fontWeight: 600
          }}
        >
          {capturedImage ? 'Preview Photo' : 'Take a Photo'}
        </Typography>
        <IconButton 
          edge="end" 
          color="inherit" 
          onClick={onClose} 
          aria-label="close"
          size={isMobile ? 'large' : 'medium'}
          sx={{
            minWidth: { xs: 48, sm: 40 },
            minHeight: { xs: 48, sm: 40 }
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      
      <DialogContent sx={{ 
        flex: 1, 
        display: 'flex', 
        flexDirection: 'column', 
        p: 0, 
        overflow: 'hidden',
        position: 'relative'
      }}>
        {error && (
          <Alert 
            severity="warning" 
            sx={{ 
              m: config.padding,
              '& .MuiAlert-message': {
                fontSize: config.bodyFontSize
              }
            }}
          >
            {error}
          </Alert>
        )}
        
        <Box 
          sx={{ 
            flex: 1,
            backgroundColor: '#000',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
            overflow: 'hidden',
            minHeight: { xs: '50vh', sm: '40vh' }
          }}
        >
          {loading && !capturedImage && (
            <CircularProgress 
              sx={{ 
                color: 'white',
                size: { xs: 48, sm: 40 }
              }} 
            />
          )}
          
          {capturedImage ? (
            // Show captured image
            <img 
              src={capturedImage} 
              alt="Captured" 
              style={{ 
                maxWidth: '100%', 
                maxHeight: '100%', 
                objectFit: 'contain',
                width: '100%',
                height: '100%'
              }} 
            />
          ) : (
            // Show video feed
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                transform: usingFrontCamera ? 'scaleX(-1)' : 'none'
              }}
            />
          )}
          
          {/* Hidden canvas for capturing photos */}
          <canvas ref={canvasRef} style={{ display: 'none' }} />
          
          {/* Camera mode indicator */}
          {!capturedImage && !loading && (
            <Typography
              variant="caption"
              sx={{
                position: 'absolute',
                bottom: { xs: 16, sm: 16 },
                left: { xs: 16, sm: 16 },
                color: 'white',
                backgroundColor: 'rgba(0,0,0,0.7)',
                padding: { xs: '6px 12px', sm: '4px 8px' },
                borderRadius: '8px',
                fontSize: { xs: '0.8rem', sm: '0.75rem' },
                fontWeight: 500
              }}
            >
              {usingFrontCamera ? 'Front Camera' : 'Back Camera'}
            </Typography>
          )}

          {/* Mobile: Floating capture button */}
          {isMobile && !capturedImage && !loading && (
            <Fab
              color="primary"
              onClick={capturePhoto}
              sx={{
                position: 'absolute',
                bottom: 20,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 64,
                height: 64,
                backgroundColor: 'white',
                color: 'primary.main',
                '&:hover': {
                  backgroundColor: 'grey.100'
                }
              }}
            >
              <CameraAlt sx={{ fontSize: '2rem' }} />
            </Fab>
          )}

          {/* Mobile: Camera switch button */}
          {isMobile && !capturedImage && !loading && (
            <IconButton
              onClick={switchCamera}
              sx={{
                position: 'absolute',
                bottom: 30,
                right: 20,
                backgroundColor: 'rgba(0,0,0,0.7)',
                color: 'white',
                width: 48,
                height: 48,
                '&:hover': {
                  backgroundColor: 'rgba(0,0,0,0.8)'
                }
              }}
            >
              <Cameraswitch />
            </IconButton>
          )}
        </Box>
      </DialogContent>
      
      {/* Desktop actions or mobile preview actions */}
      {(!isMobile || capturedImage) && (
        <DialogActions sx={{ 
          p: config.buttonPadding, 
          justifyContent: 'space-between',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 1, sm: 0 },
           // Override MUI's default button spacing
           '& > :not(style) ~ :not(style)': {
            marginLeft: 0
          },
          '& .MuiButton-root': {
            justifyContent: 'center',
            margin: 0
          },
        }}>
          {capturedImage ? (
            // Actions for preview mode
            <>
              <Button 
                variant="outlined"
                onClick={retakePhoto}
                startIcon={<CameraAlt />}
                size={config.buttonSize as any}
                fullWidth={isMobile}
                sx={{
                  fontSize: config.bodyFontSize,
                  py: { xs: 1.5, sm: 1 },
                  order: { xs: 2, sm: 0 }
                }}
              >
                Retake
              </Button>
              <Button
                variant="contained"
                color="primary"
                onClick={savePhoto}
                startIcon={<Check />}
                size={config.buttonSize as any}
                fullWidth={isMobile}
                sx={{
                  fontSize: config.bodyFontSize,
                  py: { xs: 1.5, sm: 1 },
                  order: { xs: 1, sm: 0 }
                }}
              >
                Use Photo
              </Button>
            </>
          ) : (
            // Actions for camera mode (desktop only)
            !isMobile && (
              <>
                <Button
                  variant="outlined"
                  onClick={switchCamera}
                  startIcon={<Cameraswitch />}
                  size={config.buttonSize as any}
                  sx={{
                    fontSize: config.bodyFontSize
                  }}
                >
                  Switch Camera
                </Button>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={capturePhoto}
                  disabled={loading}
                  startIcon={<CameraAlt />}
                  size={config.buttonSize as any}
                  sx={{
                    fontSize: config.bodyFontSize
                  }}
                >
                  Capture
                </Button>
              </>
            )
          )}
        </DialogActions>
      )}
    </Dialog>
  );
};

export default SimpleCamera;